import { transition, trigger, useAnimation } from '@angular/animations';
import { NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  contentChild,
  ElementRef,
  input,
  OnDestroy,
  output,
  signal,
  TemplateRef,
  viewChild,
  inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  enterAnimation,
  leaveAnimation,
} from 'src/app/shared/animations/animations';
import { ElementDimensionsDirective } from 'src/app/shared/directives/element-dimensions.directive';
import { InsightsService } from 'src/app/shared/services/insights/insights.service';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'ri-resolution-ui-lazy-load-container',
  imports: [
    ElementDimensionsDirective,
    MatButtonModule,
    NgTemplateOutlet,
    MatIcon,
  ],
  templateUrl: './lazy-load-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('loadmore', [
      transition(':enter', useAnimation(enterAnimation)),
      transition(':leave', useAnimation(leaveAnimation)),
    ]),
  ],
})
export class LazyLoadContainerComponent implements AfterViewInit, OnDestroy {
  private readonly insightsService = inject(InsightsService);

  // Input variables
  readonly intersectionObserverRootMargin =
    input<IntersectionObserverInit['rootMargin']>('0px 0px 600px 0px');
  readonly hasMoreItems = input<boolean>(false);

  // Reference variables
  private readonly containerRef = viewChild(ElementDimensionsDirective, {
    read: ElementRef,
  });
  private readonly anchorRef = viewChild<ElementRef>('anchorRef');

  // Output variables
  readonly loadMoreItems = output<void>();

  // Content Child Templates
  readonly message = contentChild<TemplateRef<unknown>>('message');

  private readonly _bottomHasBeenReached = signal<boolean>(false);
  public readonly bottomHasBeenReached =
    this._bottomHasBeenReached.asReadonly();

  private observer?: IntersectionObserver;

  ngOnDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  ngAfterViewInit() {
    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this._bottomHasBeenReached.set(true);
        } else {
          this._bottomHasBeenReached.set(false);
        }
      },
      {
        root: this.containerRef()?.nativeElement,
        rootMargin: this.intersectionObserverRootMargin(),
      }
    );

    this.observer.observe(this.anchorRef()?.nativeElement);
  }

  onLoadMore() {
    this.loadMoreItems.emit();
    this.insightsService.trackEvent('Sidebar_LoadMore_Clicked');
  }
}
